import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BannerAd = ({ position = 'home_top', className = '' }) => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchBanners();
  }, [position]);

  useEffect(() => {
    // Auto-rotate banners every 5 seconds if multiple
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/listings/banners/active?position=${position}`
      );
      setBanners(response.data.banners || []);
    } catch (err) {
      console.error('Error fetching banners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (banner) => {
    try {
      // Track click
      await axios.post(`${API_URL}/api/listings/banners/${banner.id}/click`);
      
      // Open link if exists
      if (banner.link_url) {
        window.open(banner.link_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Error tracking click:', err);
      // Still open link even if tracking fails
      if (banner.link_url) {
        window.open(banner.link_url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  if (loading || banners.length === 0 || dismissed) {
    return null;
  }

  const currentBanner = banners[currentIndex];

  // Different layouts for different positions
  if (position === 'home_sidebar') {
    return (
      <div className={`relative rounded-lg overflow-hidden shadow-md ${className}`}>
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors"
          aria-label="Dismiss ad"
        >
          <X className="h-4 w-4" />
        </button>
        <div
          onClick={() => handleClick(currentBanner)}
          className="cursor-pointer"
        >
          <img
            src={currentBanner.image_url}
            alt={currentBanner.title}
            className="w-full h-auto object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          {currentBanner.link_url && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
              <span className="text-white text-sm flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />
                Sponsored
              </span>
            </div>
          )}
        </div>
        {banners.length > 1 && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
            {banners.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default horizontal banner (home_top, category_top, etc.)
  return (
    <div className={`relative rounded-lg overflow-hidden shadow-md ${className}`}>
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors"
        aria-label="Dismiss ad"
      >
        <X className="h-4 w-4" />
      </button>
      <div
        onClick={() => handleClick(currentBanner)}
        className="cursor-pointer relative"
      >
        <img
          src={currentBanner.image_url}
          alt={currentBanner.title}
          className="w-full h-32 md:h-48 lg:h-56 object-cover"
          onError={(e) => {
            e.target.parentElement.parentElement.style.display = 'none';
          }}
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
          <div className="flex items-center justify-between">
            <span className="text-white text-xs flex items-center gap-1 opacity-75">
              <ExternalLink className="h-3 w-3" />
              Sponsored
            </span>
            {banners.length > 1 && (
              <div className="flex gap-1">
                {banners.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(idx);
                    }}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerAd;
